import { useMemo } from 'react'
import { getTaskRequest, useTask } from '~/contexts/TaskContext'

/**
 * Returns the current task's command type string.
 */
export function useTaskAwareMetadata() {
  const { task } = useTask()
  const metadata = getTaskRequest(task)?.metadata
  return {
    command: task?.cmd_type,
    codeContext: metadata?.codeContext,
    multiRepoCodeContext: metadata?.multiRepoCodeContext
  }
}

function extractProjectNameFromS3Path(s3Path?: string): string | undefined {
  if (!s3Path) return undefined
  const filename = s3Path.split('/').pop() ?? ''
  const withoutExt = filename.replace(/\.tar\.gz$/, '')
  const sepIdx = withoutExt.indexOf('___')
  return sepIdx >= 0 ? withoutExt.slice(sepIdx + 3) : withoutExt
}

/**
 * Resolves the project name for the current task.
 * Only returns a name when the user explicitly provided a project (Git repo or S3 upload).
 * Returns undefined for tasks without a user-provided project (e.g. generated from scratch).
 */
export function useProjectName(): string | undefined {
  const { task } = useTask()
  const metadata = getTaskRequest(task)?.metadata

  return useMemo(() => {
    const ctx = metadata?.codeContext
    const multiCtx = metadata?.multiRepoCodeContext

    if (ctx?.repoName) return ctx.repoName
    if (ctx?.s3TarFilePath) return extractProjectNameFromS3Path(ctx.s3TarFilePath)

    const first = multiCtx?.[0]
    if (first?.repoName) return first.repoName
    if (first?.s3TarFilePath) return extractProjectNameFromS3Path(first.s3TarFilePath)

    return undefined
  }, [metadata?.codeContext, metadata?.multiRepoCodeContext])
}

/**
 * Returns the current task object from TaskContext.
 */
export function useTaskAwareObj() {
  const { task } = useTask()
  return task
}
